import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFeedback,
  createPersona,
  createPersonaProfile,
  createPersonaSession, createSwap, deleteData, deletePersonaProfile, deleteSwap, downloadSwap,
  getCurrentPersonaSession,
  getPersonaProfiles,
  getPersonas, getSubscriptionPlans, getSwapCountInProgress,
  getUser, getUserStats, querySwaps, updatePersonaProfile, UpdatePersonaSessionBody,
  updatePersonaSessionStatus, updateSwap, updateSwapStatus, updateUser,
} from '../api';
import {
  CreateFeedbackBody,
  CreatePersonaProfileBody,
  CreatePersonaResponse,
  CreatePersonaSessionResponse,
  CreateSwapResponse,
  GetAllPersona,
  PaginationResults,
  PersonaProfileResponse,
  PersonaSessionResponse,
  SubscriptionPlansResponse,
  SwapCountInProgressResponse,
  SwapResponse,
  UpdatePersonaProfileBody,
  UpdatePersonaSessionStatusResponse,
  UpdateSwapBody,
  UpdateSwapStatusBody,
  UpdateSwapStatusResponse,
  UpdateUserBody,
  UserResponse,
  UserStatsResponse,
} from '../api/types';

export const useGetUser = () => {
  return useQuery<UserResponse>({
    queryKey: ['getUser'],
    queryFn: getUser,
    enabled: true,
  });
};

export const useGetUserStats = () => {
  return useQuery<UserStatsResponse[]>({
    queryKey: ['getUserStats'],
    queryFn: getUserStats,
    enabled: true,
  });
};

export const useGetCurrentPersonaSession = () => {
  return useQuery<PersonaSessionResponse>({
    queryKey: ['getCurrentPersonaSession'],
    queryFn: getCurrentPersonaSession,
    enabled: true,
  });
};

export const useGetSubscriptionPlans = () => {
  return useQuery<SubscriptionPlansResponse[]>({
    queryKey: ['getSubscriptionPlans'],
    queryFn: getSubscriptionPlans,
    enabled: true,
  });
};

export type CreatePersonaSessionParams = {
  contentType: string;
};
export const useCreatePersonaSession = () => {
  return useMutation<CreatePersonaSessionResponse, Error, CreatePersonaSessionParams>({
    mutationFn: (createPersonaBody) => {
      return createPersonaSession(createPersonaBody);
    },
  });
};

type UpdatePersonaSessionParams = {
  sessionId: string;
  status: string;
  failureReason?: string;
};
export const useUpdatePersonaSession = () => {
  return useMutation<UpdatePersonaSessionStatusResponse, Error, UpdatePersonaSessionParams>({
    mutationFn: ({ sessionId, status, failureReason }) => {
      const body: UpdatePersonaSessionBody = { status };
      if (failureReason) {
        body.failureReason = failureReason;
      }
      return updatePersonaSessionStatus(sessionId, body);
    },
  });
};

type UpdateSwapStatusParams = {
  swapId: string;
  status: string;
  failureReason?: string;
  isUploadFailure?: boolean;
};
export const useUpdateSwapStatus = () => {
  return useMutation<UpdateSwapStatusResponse, Error, UpdateSwapStatusParams>({
    mutationFn: ({ swapId, status, failureReason, isUploadFailure }) => {
      const body: UpdateSwapStatusBody = { status };
      if (failureReason) {
        body.failureReason = failureReason;
      }
      if (isUploadFailure) {
        body.isUploadFailure = isUploadFailure;
      }
      return updateSwapStatus(swapId, body);
    },
  });
};

type CreatePersonaParams = {
  candidateId: string;
  profileId: string;
};
export const useCreatePersona = () => {
  return useMutation<CreatePersonaResponse, Error, CreatePersonaParams>({
    mutationFn: ({ candidateId, profileId }) => {
      return createPersona({ candidateId, profileId });
    },
  });
};

export type CreateSwapsParams = {
  personaId?: string;
  swaps: {
    contentType: string;
    duration?: number | null;
  }[]
};
export const useCreateSwap = () => {
  return useMutation<CreateSwapResponse[], Error, CreateSwapsParams>({
    mutationFn: (mediaToUpload) => {
      return createSwap(mediaToUpload);
    },
  });
};

export const useQuerySwaps = (
  cursor: string | null = null,
  limit: number,
  isFavorite?: boolean,
  mediaType?: string[],
) => {
  return useQuery<PaginationResults<SwapResponse>>({
    queryKey: ['querySwaps', cursor, limit, isFavorite, mediaType],
    queryFn: () => querySwaps(cursor, limit, isFavorite, mediaType),
    enabled: true,
    staleTime: 0, // Ensure data is always considered stale, refetch on every change
  });
};

type UpdateSwapParams = {
  updateSwapBody: UpdateSwapBody;
  swapId: string;
}
export const useUpdateSwap = () => {
  return useMutation<SwapResponse, Error, UpdateSwapParams>({
    mutationFn: ({ swapId, updateSwapBody }) => {
      return updateSwap(swapId, updateSwapBody);
    },
  });
};


type DeleteSwapParams = {
  swapId: string;
};
export const useDeleteSwap = () => {
  return useMutation<void, Error, DeleteSwapParams>({
    mutationFn: ({ swapId }) => {
      return deleteSwap(swapId);
    },
  });
};

export const useGetSwapCountInProgress = () => {
  return useQuery<SwapCountInProgressResponse>({
    queryKey: ['swapCountInProgress'],
    queryFn: getSwapCountInProgress,
    enabled: true,
  });
};

type UpdateUserParams = {
  updateUserBody: UpdateUserBody;
}
export const useUpdateUser = () => {
  return useMutation<UserResponse, Error, UpdateUserParams>({
    mutationFn: ({ updateUserBody }) => {
      return updateUser(updateUserBody);
    },
  });
};


export const useDeleteData = () => {
  return useMutation<void, Error>({
    mutationFn: () => {
      return deleteData();
    },
  });
};

export const useDownloadSwap = () => {
  const queryClient = useQueryClient();

  const fetchSwap = async (swapId: string) => {
    if (!swapId) {
      throw new Error('swapId is required');
    }
    return downloadSwap(swapId);
  };

  return {
    fetchSwap,
    invalidateSwap: (swapId: string) =>
      queryClient.invalidateQueries({ queryKey: ['downloadSwap', swapId] }),
  };
};

export const useCreateFeedback = () => {
  return useMutation<{message: string}, Error, CreateFeedbackBody[]>({
    mutationFn: (createFeedbackBody) => {
      return createFeedback(createFeedbackBody);
    },
  });
};

export const useGetAllPersonas = () => {
  return useQuery<GetAllPersona[]>({
    queryKey: ['getPersonas'],
    queryFn: getPersonas,
    enabled: true,
    staleTime: 0,
  });
};

export const useGetPersonaProfiles = () => {
  return useQuery<PersonaProfileResponse[]>({
    queryKey: ['getPersonaProfiles'],
    queryFn: getPersonaProfiles,
    enabled: true,
  });
};

export const useCreatePersonaProfile = () => {
  return useMutation<PersonaProfileResponse, Error, CreatePersonaProfileBody>({
    mutationFn: (profileBody) => {
      return createPersonaProfile(profileBody);
    },
  });
};

type UpdatePersonaProfileParams = {
  profileId: string;
  updateProfileBody: UpdatePersonaProfileBody;
};
export const useUpdatePersonaProfile = () => {
  return useMutation<PersonaProfileResponse, Error, UpdatePersonaProfileParams>({
    mutationFn: ({ profileId, updateProfileBody }) => {
      return updatePersonaProfile(profileId, updateProfileBody);
    },
  });
};

type DeletePersonaProfileParams = {
  profileId: string;
};
export const useDeletePersonaProfile = () => {
  return useMutation<void, Error, DeletePersonaProfileParams>({
    mutationFn: ({ profileId }) => {
      return deletePersonaProfile(profileId);
    },
  });
};
